class SlideshowCentered extends HTMLElement {
  constructor() {
    super();
    this.mainSlide = this.querySelector(".swiper-slideshow-centered");
    this.paginationEl = this.querySelector("[data-ssc-pagination]");
    this.swiper = null;
    this.originalCount = 0;
    this.isJumping = false;
    this.autoplayEnabled = false;
    this.autoplayDelay = 5000;
    this.pauseOnMouseEnter = true;
    this.autoplayTimer = null;
    this.autoplayPaused = false;
    this.autoplayGeneration = 0;
    this.ignoreAutoplayRestartUntil = 0;
    this.init();
  }

  disconnectedCallback() {
    this.stopAutoplay();
  }

  init() {
    this.initSlideShow();
  }

  initSlideShow() {
    if (!this.mainSlide || typeof Swiper === "undefined") return;

    const wrapper = this.mainSlide.querySelector(".swiper-wrapper");
    if (!wrapper) return;

    const originals = Array.from(wrapper.children).filter((el) =>
      el.classList.contains("swiper-slide"),
    );
    this.originalCount = originals.length;
    if (this.originalCount === 0) return;

    originals.forEach((slide, index) => {
      slide.dataset.sscIndex = String(index);
    });

    this.autoplayEnabled = this.mainSlide.dataset.autoplay === "true";
    this.autoplayDelay = this.mainSlide.dataset.autoPlaySpeed
      ? Number(this.mainSlide.dataset.autoPlaySpeed)
      : 5000;
    this.pauseOnMouseEnter =
      this.mainSlide.dataset.pauseOnMouseEnter === "true";
    const loopEnabled = this.mainSlide.dataset.loop === "true";
    const spaceBetween = Number(this.mainSlide.dataset.gap) || 10;
    const canLoop = loopEnabled && this.originalCount > 1;

    let initialSlide = 0;
    if (canLoop) {
      this.duplicateSlides(wrapper, originals);
      initialSlide = this.originalCount;
    }

    this.buildPagination();

    const _this = this;
    this.swiper = new Swiper(this.mainSlide, {
      slidesPerView: "auto",
      centeredSlides: true,
      spaceBetween: spaceBetween,
      loop: false,
      initialSlide: initialSlide,
      speed: 650,
      grabCursor: true,
      allowTouchMove: true,
      threshold: 5,
      watchSlidesProgress: true,
      resistanceRatio: 0.65,
      slideToClickedSlide: true,
      // Custom autoplay below — Swiper's built-in stops after our loop jump.
      autoplay: false,
      breakpoints: {
        0: {
          spaceBetween: 0,
          speed: 380,
          threshold: 0,
          shortSwipes: true,
          longSwipesRatio: 0.2,
          longSwipesMs: 280,
          followFinger: true,
          resistanceRatio: 0.85,
          // Click-to-advance fights with swipes on iOS overlay content.
          slideToClickedSlide: false,
        },
        768: {
          spaceBetween: spaceBetween,
          speed: 650,
          threshold: 5,
          slideToClickedSlide: true,
        },
      },
      navigation: {
        nextEl: this.querySelector(".swiper-button-next"),
        prevEl: this.querySelector(".swiper-button-prev"),
      },
      on: {
        init: function () {
          _this.primeInactiveMedia(this);
          _this.snapActiveMediaSettled(this);
          _this.settleActiveText(this);
          _this.syncPagination(this);
          _this.updateTextStates(this, { resetLeavingMedia: false });
          _this.playActiveAnimations(this, { immediate: true });
          _this.mainSlide.classList.add("ssc-ready");
          _this.startAutoplay();
        },
        slideChangeTransitionStart: function () {
          if (_this.isJumping) return;
          // Always clear timer + fill at the start of a move so nav clicks
          // and leftover timeouts can't fight the next startAutoplay.
          _this.stopAutoplay();
          _this.syncPagination(this);
          _this.updateTextStates(this, { resetLeavingMedia: false });
          _this.easeOutLeavingMedia(this);
          // Wrap: keep clone + real twin locked at the primed zoom (1.1),
          // same as a normal incoming slide — Ken Burns runs after jumpTo.
          if (_this.isWrapIndex(this.activeIndex)) {
            _this.snapMediaZoomed(this.slides[this.activeIndex]);
            const twin = _this.wrapTwinSlide(this, this.activeIndex);
            if (twin) _this.snapMediaZoomed(twin);
          }
          // Desktop only: hide incoming text for fade-up entrance.
          if (window.innerWidth >= 768) {
            _this.prepareIncomingText(this);
          }
        },
        slideChangeTransitionEnd: function () {
          if (_this.isJumping) return;
          // Wrap jump handles sync + Ken Burns + autoplay itself.
          if (_this.normalizePosition(this)) return;
          _this.syncPagination(this);
          _this.updateTextStates(this, { resetLeavingMedia: false });
          _this.playActiveAnimations(this);
          if (performance.now() < _this.ignoreAutoplayRestartUntil) return;
          _this.startAutoplay();
        },
        // Real drag only — not every touchStart (those were resetting the bullet).
        sliderFirstMove: function () {
          _this.stopAutoplay();
        },
      },
    });

    // Don't pause on hover: with overlaid controls the cursor is often over the
    // section, which cleared is-timing and left the fill frozen until mouseleave.
    return this.swiper;
  }

  startAutoplay() {
    // Clear any prior timer/fill without bumping generation twice.
    if (this.autoplayTimer) {
      window.clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
    }
    this.paginationEl
      ?.querySelectorAll(".ssc__dot.is-timing")
      .forEach((dot) => dot.classList.remove("is-timing"));

    if (!this.autoplayEnabled || this.originalCount < 2) {
      return;
    }

    const generation = ++this.autoplayGeneration;
    const activeDot = this.paginationEl?.querySelector(".ssc__dot.is-active");
    if (activeDot) {
      activeDot.classList.remove("is-timing");
      void activeDot.offsetWidth;
      activeDot.classList.add("is-timing");
    }

    this.autoplayTimer = window.setTimeout(() => {
      if (generation !== this.autoplayGeneration) return;
      this.autoplayTimer = null;
      this.advanceSlide();
    }, this.autoplayDelay);
  }

  stopAutoplay() {
    this.autoplayGeneration += 1;
    if (this.autoplayTimer) {
      window.clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
    }
    this.paginationEl
      ?.querySelectorAll(".ssc__dot.is-timing")
      .forEach((dot) => dot.classList.remove("is-timing"));
  }

  advanceSlide() {
    if (!this.swiper || this.isJumping) {
      this.startAutoplay();
      return;
    }

    const n = this.originalCount;
    const idx = this.swiper.activeIndex;
    // Middle set is [n, 2n). Last real slide → first clone of set 2 (forward),
    // then normalizePosition jumps back to the real first slide.
    const lastReal = n * 2 - 1;
    let target;

    if (idx >= n && idx < lastReal) {
      target = idx + 1;
    } else if (idx === lastReal) {
      target = n * 2;
    } else {
      const logical = this.logicalIndex(this.swiper);
      target = n + ((logical + 1) % n);
    }

    this.swiper.slideTo(target);
  }

  duplicateSlides(wrapper, originals) {
    const before = document.createDocumentFragment();
    const after = document.createDocumentFragment();

    originals.forEach((slide) => {
      before.appendChild(this.cloneSlide(slide));
      after.appendChild(this.cloneSlide(slide));
    });

    wrapper.insertBefore(before, originals[0]);
    wrapper.appendChild(after);
  }

  cloneSlide(slide) {
    const clone = slide.cloneNode(true);
    clone.classList.add("ssc-clone");
    clone.removeAttribute("id");
    clone.querySelectorAll(".ssc__content.is-active").forEach((el) => {
      el.classList.remove("is-active");
    });
    clone.querySelectorAll("motion-effect").forEach((motion) => {
      motion.classList.add("animate-delay");
    });
    return clone;
  }

  logicalIndex(swiper) {
    if (this.originalCount <= 0) return 0;
    return (
      ((swiper.activeIndex % this.originalCount) + this.originalCount) %
      this.originalCount
    );
  }

  isWrapIndex(index) {
    if (this.originalCount <= 1) return false;
    const n = this.originalCount;
    return index < n || index >= n * 2;
  }

  normalizePosition(swiper) {
    if (this.originalCount <= 1) return false;
    const n = this.originalCount;
    const idx = swiper.activeIndex;
    if (idx < n) {
      this.jumpTo(swiper, idx + n);
      return true;
    }
    if (idx >= n * 2) {
      this.jumpTo(swiper, idx - n);
      return true;
    }
    return false;
  }

  jumpTo(swiper, index) {
    this.isJumping = true;
    // Invisible jump: both frames must share the primed zoom, then Ken Burns
    // runs exactly like a normal second→first landing.
    this.snapMediaZoomed(swiper.slides[swiper.activeIndex]);
    this.snapMediaZoomed(swiper.slides[index]);
    swiper.slideTo(index, 0, false);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.isJumping = false;
        this.syncPagination(swiper);
        this.updateTextStates(swiper, { resetLeavingMedia: false });
        this.playActiveAnimations(swiper);
        this.startAutoplay();
        this.ignoreAutoplayRestartUntil = performance.now() + 120;
      });
    });
  }

  buildPagination() {
    if (!this.paginationEl || this.originalCount < 1) return;
    this.paginationEl.innerHTML = "";

    for (let i = 0; i < this.originalCount; i += 1) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = `ssc__dot${i === 0 ? " is-active" : ""}`;
      btn.setAttribute("aria-label", `Go to slide ${i + 1}`);
      btn.dataset.index = String(i);
      btn.addEventListener("click", () => {
        if (!this.swiper) return;
        this.stopAutoplay();
        const target =
          this.originalCount > 1 ? this.originalCount + i : i;
        this.swiper.slideTo(target);
      });
      this.paginationEl.appendChild(btn);
    }
  }

  syncPagination(swiper) {
    if (!this.paginationEl) return;
    const active = this.logicalIndex(swiper);
    this.paginationEl.querySelectorAll(".ssc__dot").forEach((dot, index) => {
      const shouldActive = index === active;
      if (shouldActive) {
        if (!dot.classList.contains("is-active")) {
          dot.classList.add("is-active");
        }
      } else {
        dot.classList.remove("is-active", "is-timing");
      }
    });
  }

  textMotions(slide) {
    if (!slide) return [];
    return Array.from(
      slide.querySelectorAll(".ssc__content motion-effect[slideshow]"),
    );
  }

  mediaMotions(slide) {
    if (!slide) return [];
    return Array.from(
      slide.querySelectorAll(".ssc__media motion-effect[slideshow]"),
    );
  }

  snapMediaZoomed(slide) {
    if (!slide || window.innerWidth < 768) return;
    this.mediaMotions(slide).forEach((motion) => {
      motion.classList.add("animate-delay");
      if (typeof Motion !== "undefined") {
        Motion.animate(
          motion,
          { transform: "scale(1.1)", opacity: 1 },
          { duration: 0 },
        );
      } else {
        motion.style.transform = "scale(1.1)";
        motion.style.opacity = "1";
      }
    });
  }

  snapMediaSettled(slide) {
    if (!slide) return;
    this.mediaMotions(slide).forEach((motion) => {
      motion.classList.add("animate-delay");
      motion.classList.remove("loading-motion", "loading");
      if (typeof Motion !== "undefined") {
        Motion.animate(
          motion,
          { transform: "scale(1)", opacity: 1 },
          { duration: 0 },
        );
      } else {
        motion.style.transform = "scale(1)";
        motion.style.opacity = "1";
      }
    });
  }

  /** First paint: active image already at scale(1) — no entrance zoom. */
  snapActiveMediaSettled(swiper) {
    this.snapMediaSettled(swiper.slides[swiper.activeIndex]);
  }

  /**
   * Zoom the leaving slide's media back out while it slides away —
   * avoids the hard snap that used to run on transition end.
   */
  easeOutLeavingMedia(swiper) {
    if (window.innerWidth < 768 || typeof Motion === "undefined") return;

    const prev = swiper.slides[swiper.previousIndex];
    const next = swiper.slides[swiper.activeIndex];
    if (!prev || prev === next) return;

    const duration = Math.max((swiper.params?.speed || 650) / 1000, 0.35);

    this.mediaMotions(prev).forEach((motion) => {
      motion.classList.add("animate-delay");
      Motion.animate(
        motion,
        { transform: "scale(1.1)", opacity: 1 },
        {
          duration,
          easing: [0.22, 1, 0.36, 1],
        },
      );
    });
  }

  wrapTwinSlide(swiper, index) {
    if (!this.isWrapIndex(index)) return null;
    const n = this.originalCount;
    const twinIndex = index >= n * 2 ? index - n : index + n;
    return swiper.slides[twinIndex] || null;
  }

  snapTextHidden(slide) {
    if (window.innerWidth < 768) {
      // Skip Motion reverse on iOS — keeps swipe transitions smooth.
      this.textMotions(slide).forEach((motion) => {
        motion.classList.add("animate-delay");
        motion.style.opacity = "0";
        motion.style.transform = "none";
      });
      return;
    }
    this.textMotions(slide).forEach((motion) => {
      motion.classList.add("animate-delay");
      if (typeof motion.initAnimate === "function") {
        motion.initAnimate();
      } else if (typeof Motion !== "undefined") {
        Motion.animate(
          motion,
          { transform: "translateY(30px)", opacity: 0 },
          { duration: 0 },
        );
      }
    });
  }

  /** Kill Motion fade-up race on first paint (slide 1 was flickering). */
  settleTextVisible(slide) {
    if (!slide) return;
    this.textMotions(slide).forEach((motion) => {
      motion.classList.add("animate-delay");
      motion.classList.remove("loading-motion", "loading");
      if (typeof Motion !== "undefined") {
        Motion.animate(
          motion,
          { opacity: 1, transform: "none" },
          { duration: 0 },
        );
      }
      motion.style.opacity = "1";
      motion.style.transform = "none";
    });
  }

  settleActiveText(swiper) {
    const slide = swiper.slides[swiper.activeIndex];
    this.settleTextVisible(slide);
  }

  primeInactiveMedia(swiper) {
    swiper.slides.forEach((slide) => {
      if (slide.classList.contains("swiper-slide-active")) {
        if (window.innerWidth < 768) this.settleTextVisible(slide);
        return;
      }
      this.snapMediaZoomed(slide);
      if (window.innerWidth >= 768) {
        this.snapTextHidden(slide);
      } else {
        this.settleTextVisible(slide);
      }
    });
  }

  updateTextStates(swiper, { resetLeavingMedia = false } = {}) {
    const isMobile = window.innerWidth < 768;

    swiper.slides.forEach((slide) => {
      const isActive = slide.classList.contains("swiper-slide-active");
      const content = slide.querySelector(".ssc__content");
      if (!content) return;

      if (isMobile) {
        // Keep all copy visible; is-active only gates button taps.
        content.classList.toggle("is-active", isActive);
        content.classList.remove("is-leaving");
        this.settleTextVisible(slide);
        return;
      }

      if (isActive) {
        content.classList.add("is-active");
        content.classList.remove("is-leaving");
        return;
      }

      if (content.classList.contains("is-active")) {
        content.classList.add("is-leaving");
        content.classList.remove("is-active");
        this.textMotions(slide).forEach((motion) => {
          if (typeof motion._reverseToInitial === "function") {
            motion._reverseToInitial();
          } else {
            this.snapTextHidden(slide);
          }
          motion.classList.add("animate-delay");
        });
        return;
      }

      content.classList.remove("is-active", "is-leaving");
      if (resetLeavingMedia) {
        this.snapMediaZoomed(slide);
      }
    });
  }

  prepareIncomingText(swiper) {
    const currentSlide = swiper.slides[swiper.activeIndex];
    if (!currentSlide) return;
    this.snapTextHidden(currentSlide);
  }

  playActiveAnimations(swiper, { immediate = false } = {}) {
    const run = () => {
      const slide = swiper.slides[swiper.activeIndex];
      if (!slide) return;
      const isMobile = window.innerWidth < 768;

      const content = slide.querySelector(".ssc__content");
      if (content) {
        content.classList.add("is-active");
        content.classList.remove("is-leaving");
      }

      // First paint / mobile: show text immediately — no fade-up flicker.
      if (immediate || isMobile) {
        this.settleTextVisible(slide);
      } else {
        this.textMotions(slide).forEach(async (motion) => {
          motion.classList.remove("animate-delay");
          if (typeof motion.initAnimateEffect === "function") {
            await motion.initAnimateEffect();
          }
        });
      }

      this.mediaMotions(slide).forEach((motion) => {
        // Keep animate-delay so MotionEffect inView never re-triggers zoom.
        motion.classList.add("animate-delay");
        motion.classList.remove("loading-motion", "loading");
        if (isMobile || typeof Motion === "undefined") {
          motion.style.transform = "scale(1)";
          motion.style.opacity = "1";
          return;
        }

        if (immediate) {
          Motion.animate(
            motion,
            { transform: "scale(1)", opacity: 1 },
            { duration: 0 },
          );
          return;
        }

        Motion.animate(
          motion,
          { transform: "scale(1)", opacity: 1 },
          {
            duration: 0.85,
            easing: [0.22, 1, 0.36, 1],
          },
        );
      });
    };

    if (immediate) {
      run();
    } else {
      requestAnimationFrame(() => setTimeout(run, 30));
    }
  }
}

if (!customElements.get("slideshow-centered")) {
  customElements.define("slideshow-centered", SlideshowCentered);
}
